import React from 'react';
import { Download, FileText, CheckCircle } from 'lucide-react';
import { apiService } from '../services/ApiService';
import { exportToPDF } from '../utils/exportPDF';
import { exportToJSON } from '../utils/exportJSON';
import { ScanResult, ScanSummary } from '../types';

interface ExportButtonsProps {
    result: ScanResult;
    summary: ScanSummary;
    plan?: 'free' | 'starter' | 'pro' | 'team';
}

export const ExportButtons: React.FC<ExportButtonsProps> = ({ result, summary }) => {
    const [isPaying, setIsPaying] = React.useState(false);
    const [isPaid, setIsPaid] = React.useState(result.isPaid || false);

    const checkPaymentStatus = React.useCallback(async () => {
        try {
            const status = await apiService.verifyPayment(result.id!);
            setIsPaid(status.isPaid);
        } catch (error) {
            console.error('Failed to verify payment:', error);
        }
    }, [result.id]);

    React.useEffect(() => {
        setIsPaid(result.isPaid || false);
        if (result.id && !result.isPaid) {
            checkPaymentStatus();
        }
    }, [result.id, result.isPaid, checkPaymentStatus]);

    const handleBuyPDF = async () => {
        setIsPaying(true);
        try {
            const session = await apiService.createCheckoutSession(result.id!);
            // In a real app, redirect to session.url
            // For this environment, we'll simulate the success
            window.location.href = session.url;
        } catch (error) {
            console.error('Payment failed:', error);
            alert('Failed to initialize payment. Please try again.');
        } finally {
            setIsPaying(false);
        }
    };

    return (
        <div className="flex space-x-3">
            {/* PDF Export */}
            {isPaid ? (
                <button
                    onClick={() => exportToPDF(result)}
                    className="flex items-center px-4 py-2 border border-green-200 text-green-700 bg-green-50 hover:bg-green-100 rounded-md shadow-sm text-sm font-medium transition-colors"
                >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Download PDF
                </button>
            ) : (
                <button
                    onClick={handleBuyPDF}
                    disabled={isPaying}
                    className="flex items-center px-4 py-2 border border-primary-200 text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-md shadow-sm text-sm font-medium transition-colors disabled:opacity-50"
                >
                    {isPaying ? (
                        <div className="loading-spinner h-4 w-4 mr-2" />
                    ) : (
                        <FileText className="w-4 h-4 mr-2" />
                    )}
                    Get PDF ($5)
                </button>
            )}

            {/* JSON Export (Free for now in open-access) */}
            <button
                onClick={() => exportToJSON(result)}
                className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded-md shadow-sm text-sm font-medium transition-colors"
            >
                <Download className="w-4 h-4 mr-2" />
                Raw JSON
            </button>
        </div>
    );
};
